import { useState } from "react";
import { Link } from "react-router";
import { ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { WhatsAppTrechoInline } from "@/components/whatsapp/WhatsAppTrechoInline";
import { formatDateBR, formatDateTime } from "@/utils/dateUtils";
import type { DiffCampo, TimelineEvento } from "@/types/timeline";
import { TimelineEventDot } from "./TimelineEventDot";
import { formatMotivosInline } from "@/utils/motivosUtils";
import {
  ATOR_ICON,
  atorDescricao,
  campoLabel,
  CONFIANCA_LABEL,
  formatEventoQuando,
  INFERRED_HINT,
  linkedinTransicaoDetalhe,
  origemChipLabel,
  valorDiffTexto,
} from "./timelineEventUtils";

const LIMITE_TEXTO_CURTO = 180;

/** Valor de diff com "ver mais" para textos longos e "•••" para mascarados. */
function ValorDiff({ valor, mascarado, truncado }: { valor: unknown; mascarado?: boolean; truncado?: boolean }) {
  const [expandido, setExpandido] = useState(false);
  if (mascarado) return <span className="text-muted-foreground">•••</span>;
  const texto = valorDiffTexto(valor);
  const longo = texto.length > LIMITE_TEXTO_CURTO;
  const exibido = !longo || expandido ? texto : `${texto.slice(0, LIMITE_TEXTO_CURTO)}...`;
  return (
    <span className="wrap-break-word">
      {exibido}
      {truncado && (
        <span className="text-xs text-muted-foreground"> (texto encurtado no registro)</span>
      )}
      {longo && (
        <button
          type="button"
          className="ml-1 text-xs text-primary underline-offset-2 hover:underline"
          onClick={() => setExpandido((atual) => !atual)}
        >
          {expandido ? "ver menos" : "ver mais"}
        </button>
      )}
    </span>
  );
}

/** Tabela Campo | Antes | Depois do diff de perfil_atualizado (§4.2). */
function TabelaDiff({ diff }: { diff: Record<string, DiffCampo> }) {
  const campos = Object.keys(diff);
  if (!campos.length) return null;
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">Campo</th>
            <th className="px-3 py-2 font-medium">Antes</th>
            <th className="px-3 py-2 font-medium">Depois</th>
          </tr>
        </thead>
        <tbody>
          {campos.map((campo) => {
            const mudanca = diff[campo];
            return (
              <tr key={campo} className="border-b last:border-b-0 align-top">
                <td className="px-3 py-2 font-medium whitespace-nowrap">{campoLabel(campo)}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  <ValorDiff valor={mudanca?.de} mascarado={mudanca?.mascarado} />
                </td>
                <td className="px-3 py-2">
                  <ValorDiff
                    valor={mudanca?.para}
                    mascarado={mudanca?.mascarado}
                    truncado={mudanca?.truncado}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Linha rotulada do corpo do dialog. */
function LinhaDetalhe({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-24 shrink-0 text-muted-foreground">{rotulo}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

interface EventDetailDialogProps {
  evento: TimelineEvento | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Detalhe de um evento da linha do tempo (§4.2): quando, por quem, chip de
 * origem; diff Campo | Antes | Depois quando houver; links reais (<Link>) para
 * a vaga, o freelancer (quando o evento vem da timeline da agência, onde cada
 * marco pode ser de uma pessoa diferente) e para campanhas (cmd/ctrl+clique
 * abre em nova aba).
 */
export function EventDetailDialog({ evento, open, onOpenChange }: EventDetailDialogProps) {
  const [trechoAberto, setTrechoAberto] = useState(false);
  if (!evento) return null;

  const AtorIcon = ATOR_ICON[evento.actor_kind] ?? ATOR_ICON.sistema;
  const origemLabel = origemChipLabel(evento);
  const diff = evento.payload?.diff;
  const motivoDrop = evento.payload?.motivo_drop;
  const motivoDropTexto = formatMotivosInline(motivoDrop);
  // Mudança vista no LinkedIn: data_inicio vem como "YYYY-MM", que os campos de projeto não leem.
  const linkedinDetalhe = linkedinTransicaoDetalhe(evento);
  const ehMudancaLinkedin = evento.event_type.startsWith("linkedin_");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TimelineEventDot evento={evento} size="md" />
            {evento.label}
          </DialogTitle>
          {evento.description && <DialogDescription>{evento.description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-3">
          {/* Cabeçalho: quando, por quem, origem */}
          <LinhaDetalhe rotulo="Quando">
            <span>{formatEventoQuando(evento.occurred_at, true)}</span>
          </LinhaDetalhe>
          <LinhaDetalhe rotulo="Por quem">
            <span className="inline-flex items-center gap-1.5">
              <AtorIcon className="h-3.5 w-3.5 text-muted-foreground" />
              {atorDescricao(evento)}
            </span>
          </LinhaDetalhe>
          {(origemLabel || evento.inferred) && (
            <LinhaDetalhe rotulo="Origem">
              <span className="flex flex-wrap items-center gap-1.5">
                {origemLabel && <Badge variant="outline">{origemLabel}</Badge>}
                {evento.inferred && (
                  <Badge
                    variant="outline"
                    className="border-dashed border-amber-400 bg-amber-50 text-amber-700"
                    title={INFERRED_HINT}
                  >
                    Reconstruído do histórico
                    {evento.confidence !== "alta" &&
                      ` (confiança ${CONFIANCA_LABEL[evento.confidence] ?? evento.confidence})`}
                  </Badge>
                )}
              </span>
            </LinhaDetalhe>
          )}

          {/* Evento que veio de conversa: a evidência fica a um clique, sem sair
              da linha do tempo (o source_ref é gravado pelo applier do WhatsApp) */}
          {typeof evento.source_ref?.item_id === "string" &&
            typeof evento.source_ref?.conversation_id === "string" && (
              <LinhaDetalhe rotulo="Conversa">
                <div className="w-full">
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    onClick={() => setTrechoAberto((aberto) => !aberto)}
                  >
                    {trechoAberto ? "Esconder trecho" : "Ver trecho da conversa"}
                  </Button>
                  {trechoAberto && (
                    <WhatsAppTrechoInline
                      itemId={evento.source_ref.item_id as string}
                      conversationId={evento.source_ref.conversation_id as string}
                    />
                  )}
                </div>
              </LinhaDetalhe>
            )}

          {/* Freelancer sujeito do evento: só aparece quando o dado veio enriquecido
              (timeline da agência) — na timeline do próprio freelancer seria um link
              redundante para a página em que já se está. */}
          {evento.freelancer_id && evento.freelancer_nome && (
            <LinhaDetalhe rotulo="Freelancer">
              <Link
                to={`/freelancers/${evento.freelancer_id}`}
                className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
              >
                {evento.freelancer_nome}
                <ExternalLink className="h-3 w-3" />
              </Link>
            </LinhaDetalhe>
          )}

          {/* Vaga relacionada: link real, cmd+clique abre nova aba */}
          {evento.vaga_id && (
            <LinhaDetalhe rotulo="Vaga">
              <Link
                to={`/vagas/${evento.vaga_id}`}
                className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
              >
                {evento.vaga_titulo ?? "Ver vaga"}
                <ExternalLink className="h-3 w-3" />
              </Link>
              {evento.payload?.etapa && (
                <span className="ml-2 text-xs text-muted-foreground">
                  etapa: {evento.payload.etapa}
                </span>
              )}
            </LinhaDetalhe>
          )}
          {motivoDropTexto && (
            <LinhaDetalhe rotulo="Motivo">
              <span>{motivoDropTexto}</span>
            </LinhaDetalhe>
          )}

          {/* Campanha relacionada */}
          {evento.campanha_nome && (
            <LinhaDetalhe rotulo="Campanha">
              <Link
                to="/campanhas"
                className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
              >
                {evento.campanha_nome}
                <ExternalLink className="h-3 w-3" />
              </Link>
            </LinhaDetalhe>
          )}

          {linkedinDetalhe && (
            <LinhaDetalhe rotulo="No LinkedIn">
              <span>{linkedinDetalhe}</span>
            </LinhaDetalhe>
          )}

          {/* Projeto: datas lógicas (YYYY-MM-DD) via formatDateBR */}
          {!ehMudancaLinkedin && evento.payload?.data_inicio && (
            <LinhaDetalhe rotulo="Início">
              <span>{formatDateBR(String(evento.payload.data_inicio))}</span>
            </LinhaDetalhe>
          )}
          {!ehMudancaLinkedin && evento.payload?.data_conclusao && (
            <LinhaDetalhe rotulo="Conclusão">
              <span>{formatDateBR(String(evento.payload.data_conclusao))}</span>
            </LinhaDetalhe>
          )}

          {/* Avaliação: nota recebida */}
          {typeof evento.payload?.rating === "number" && (
            <LinhaDetalhe rotulo="Nota">
              <span>
                {evento.payload.rating}
                {typeof evento.payload?.media_geral === "number" && (
                  <span className="text-muted-foreground">
                    {" "}
                    (média geral {evento.payload.media_geral})
                  </span>
                )}
              </span>
            </LinhaDetalhe>
          )}

          {/* Diff de atualização de cadastro */}
          {diff && Object.keys(diff).length > 0 && (
            <div className="space-y-1.5 pt-1">
              <h3 className="text-sm font-medium text-muted-foreground">
                O que mudou
                {typeof evento.payload?.n_campos === "number" &&
                  ` (${evento.payload.n_campos} ${evento.payload.n_campos === 1 ? "campo" : "campos"})`}
              </h3>
              <TabelaDiff diff={diff} />
            </div>
          )}

          {/* Quando soubemos, se difere do quando aconteceu (ex.: backfill) */}
          {evento.recorded_at &&
            new Date(evento.recorded_at).getTime() - new Date(evento.occurred_at).getTime() >
              24 * 60 * 60 * 1000 && (
              <p className="text-xs text-muted-foreground">
                Registrado no sistema em {formatDateTime(evento.recorded_at)}.
              </p>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EventDetailDialog;
